import {
  getNewOrderDefaultState,
  newOrderDefaultState,
  NewOrderLsKeys,
  NewOrderScenario,
  TNewOrderForm,
} from "@/entities/new-order";
import {
  getExtendDraft,
  getParsedAttributes,
} from "@/entities/new-order/utils";
import { useLazyGetAppInfoQuery } from "@/Store/APIs/Apps.api";
import { useLazyGetCampaignInfoQuery } from "@/Store/APIs/Campaign.api";
import { useDebouncedCallback } from "@mantine/hooks";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

/**
 * Хук для управления формой продления заказа
 * Загружает данные существующего заказа и формирует новый заказ с продлением сроков
 */
export function useExtendedOrderForm() {
  const { id: orderId } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);

  const [extendDrafts, setExtendDrafts] = useLocalStorage<
    Record<string, TNewOrderForm>
  >(NewOrderLsKeys.EXTEND_ORDER_DRAFT, {});

  const methods = useForm<TNewOrderForm>({
    defaultValues: newOrderDefaultState,
  });

  const { watch, getValues } = methods;

  const [getAppInfo] = useLazyGetAppInfoQuery();
  const [getOrderInfo] = useLazyGetCampaignInfoQuery();

  // Ref для отслеживания размонтирования компонента
  const isMountedRef = useRef(true);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Получает состояние формы для продления заказа
   * Загружает данные заказа и формирует новый черновик с продленными датами
   */
  const getDefaultFormState = async (orderId: string) => {
    // Создаем свежую копию дефолтного состояния
    const formState: TNewOrderForm = getNewOrderDefaultState();

    try {
      const campaign = await getOrderInfo(orderId).unwrap();

      if (!campaign) {
        return formState;
      }

      const appInfo = await getAppInfo(campaign.app).unwrap();
      const attributes = getParsedAttributes(campaign.settings_message);

      // Формируем черновик с новыми датами и скопированными данными
      const {
        dateStart,
        dateStop,
        delayed_start,
        delayedStartValue,
        countriesList,
      } = getExtendDraft(campaign);

      return {
        ...formState,
        type: campaign.type,
        app: appInfo,
        dateStart,
        dateStop,
        attributesList: attributes,
        delayedStart: {
          value: delayedStartValue,
          enable: delayed_start,
        },
        countriesList,
      };
    } catch (error) {
      console.error("Error loading extend order data:", error);
      // Возвращаем базовое состояние при ошибке
      return formState;
    }
  };

  // Инициализация формы при монтировании или смене orderId
  useEffect(() => {
    let isCurrentEffect = true;

    const initializeForm = async () => {
      try {
        if (!orderId) {
          return;
        }

        let newOrderState: TNewOrderForm;

        // Проверяем наличие сохраненного черновика
        const existingDraft = extendDrafts[orderId];

        if (existingDraft) {
          // Если черновик есть - используем его
          newOrderState = existingDraft;
        } else {
          // Если черновика нет - загружаем данные с сервера
          newOrderState = await getDefaultFormState(orderId);
        }

        // Проверяем, что компонент еще смонтирован
        if (!isCurrentEffect || !isMountedRef.current) {
          return;
        }

        methods.reset(
          {
            ...newOrderState,
            activeScenario: NewOrderScenario.extend,
          },
          { keepDefaultValues: false },
        );
      } catch (error) {
        console.error("Error initializing extend order form:", error);
      } finally {
        if (isCurrentEffect && isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    initializeForm();

    // Cleanup function для отмены эффекта если orderId изменился
    return () => {
      isCurrentEffect = false;
    };
  }, [orderId]);

  // Автосохранение черновика с задержкой
  const onSave = useDebouncedCallback((data: TNewOrderForm) => {
    // Проверяем, что компонент смонтирован
    if (!isMountedRef.current) {
      return;
    }

    if (orderId) {
      setExtendDrafts({
        ...extendDrafts,
        [orderId]: data,
      });
    }
  }, 500);

  // Эффект для автосохранения формы
  useEffect(() => {
    const subscription = watch(() => {
      if (isMountedRef.current) {
        const formData = getValues();
        onSave(formData);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, getValues, onSave]);

  /**
   * Очищает сохраненный черновик для текущего заказа
   */
  const clearDraft = () => {
    if (!orderId) {
      return;
    }

    const updatedDrafts = { ...extendDrafts };
    delete updatedDrafts[orderId];
    setExtendDrafts(updatedDrafts);
  };

  /**
   * Сбрасывает форму к начальному состоянию
   * Удаляет черновик и загружает свежие данные с сервера
   */
  const handleReset = async () => {
    if (!orderId) {
      return;
    }

    setIsLoading(true);

    try {
      const resetFormState = await getDefaultFormState(orderId);

      clearDraft();
      methods.reset(
        {
          ...resetFormState,
          activeScenario: NewOrderScenario.extend,
        },
        { keepDefaultValues: false },
      );
    } catch (error) {
      console.error("Error resetting extend order form:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    methods,
    handleReset,
    isLoading,
  };
}
